import { Controller } from "@hotwired/stimulus"
import L from "leaflet"

export default class extends Controller {
  static targets = [ "list", "mapPanel", "map", "listButton", "mapButton" ]
  static values = {
    listings: Array,
    initialMode: { type: String, default: "list" }
  }

  connect() {
    if (this.initialModeValue === "map") {
      this.showMap()
    } else {
      this.showList()
    }
  }

  disconnect() {
    this.mapInstance?.remove()
    this.mapInstance = null
  }

  showList() {
    this.listTarget.classList.remove("hidden")
    this.mapPanelTarget.classList.add("hidden")
    this.setActiveButton("list")
    this.updateUrl("list")
  }

  showMap() {
    this.listTarget.classList.add("hidden")
    this.mapPanelTarget.classList.remove("hidden")
    this.setActiveButton("map")
    this.updateUrl("map")

    if (!this.hasMapTarget || this.listingsValue.length === 0) return

    this.initializeMap()
    requestAnimationFrame(() => this.mapInstance.invalidateSize())
  }

  initializeMap() {
    if (this.mapInstance) return

    this.mapInstance = L.map(this.mapTarget, { zoomControl: true })
    L.tileLayer("https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png", {
      attribution: '&copy; <a href="https://carto.com/attributions">CARTO</a> &copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
    }).addTo(this.mapInstance)

    const bounds = []
    this.listingsValue.forEach((listing) => {
      const position = [ listing.latitude, listing.longitude ]
      bounds.push(position)
      L.marker(position, { icon: this.markerIcon(listing.visited) })
        .bindPopup(this.popupFor(listing), { minWidth: 230, maxWidth: 300 })
        .addTo(this.mapInstance)
    })

    if (bounds.length === 1) {
      this.mapInstance.setView(bounds[0], 15)
    } else {
      this.mapInstance.fitBounds(bounds, { padding: [ 32, 32 ], maxZoom: 16 })
    }
  }

  markerIcon(visited) {
    const color = visited ? "#059669" : "#2b6cee"
    return L.divIcon({
      html: `<span style="display:block;width:24px;height:24px;border-radius:9999px;background:${color};border:4px solid white;box-shadow:0 3px 12px rgba(15,23,42,.35)"></span>`,
      className: "",
      iconSize: [ 24, 24 ],
      iconAnchor: [ 12, 12 ],
      popupAnchor: [ 0, -12 ]
    })
  }

  popupFor(listing) {
    const container = document.createElement("article")
    container.className = "overflow-hidden text-slate-900"

    if (listing.photoUrl) {
      const image = document.createElement("img")
      image.src = listing.photoUrl
      image.alt = ""
      image.loading = "lazy"
      image.className = "mb-3 h-28 w-full rounded-lg object-cover"
      container.appendChild(image)
    }

    const title = document.createElement("h3")
    title.textContent = listing.title
    title.className = "font-bold leading-5"
    container.appendChild(title)

    const address = document.createElement("p")
    address.textContent = listing.address
    address.className = "mt-1 text-xs leading-5 text-slate-600"
    container.appendChild(address)

    const facts = document.createElement("p")
    facts.textContent = [ listing.price, listing.surface ].filter(Boolean).join(" · ") || "Informations à compléter"
    facts.className = "mt-2 text-sm font-bold"
    container.appendChild(facts)

    if (listing.visited) {
      const visit = document.createElement("p")
      visit.textContent = `Visité · ${"★".repeat(listing.rating)}`
      visit.className = "mt-2 text-xs font-bold text-emerald-700"
      container.appendChild(visit)
    }

    const link = document.createElement("a")
    link.href = listing.url
    link.textContent = "Ouvrir l’annonce"
    link.className = "mt-3 flex min-h-10 items-center justify-center rounded-lg bg-[#2b6cee] px-3 text-sm font-bold text-white"
    container.appendChild(link)

    return container
  }

  setActiveButton(mode) {
    const listActive = mode === "list"
    this.listButtonTarget.setAttribute("aria-pressed", String(listActive))
    this.mapButtonTarget.setAttribute("aria-pressed", String(!listActive))
    this.listButtonTarget.classList.toggle("bg-white", listActive)
    this.listButtonTarget.classList.toggle("text-slate-950", listActive)
    this.listButtonTarget.classList.toggle("shadow-sm", listActive)
    this.mapButtonTarget.classList.toggle("bg-white", !listActive)
    this.mapButtonTarget.classList.toggle("text-slate-950", !listActive)
    this.mapButtonTarget.classList.toggle("shadow-sm", !listActive)
  }

  updateUrl(mode) {
    const url = new URL(window.location.href)
    if (mode === "map") {
      url.searchParams.set("display", "map")
    } else {
      url.searchParams.delete("display")
    }
    window.history.replaceState(window.history.state, "", url)
  }
}
